import { useState, useEffect } from "react";
import {
  getCompanyDetailApi,
  getCompanyNielsenApi,
  getCompanyCreaApi,
} from "../api/company.api";
import { getEntityNewsForEntityApi } from "../api/entityNews.api";
import { searchWorkRelationApi } from "../api/search.api";
import { headersBuilder } from "../libs/helpers/callApi";

const useMultipleCompanyDetails = (ids) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ids || ids.length === 0) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    const fetchNeeded = async () => {
      // Find which IDs we don't have yet
      const existingIds = companies.map((c) => c.id);
      const newIds = ids.filter((id) => !existingIds.includes(id));

      // If we only removed IDs or rearranged them, just update state and order
      if (newIds.length === 0) {
        setCompanies((prev) => {
          const filtered = prev.filter((c) => ids.includes(c.id));
          // Re-order to match the current 'ids' array
          return ids.map((id) => filtered.find((c) => c.id === id));
        });
        return;
      }

      // If we have new IDs, we fetch only those
      setLoading(true);
      setError(null);
      try {
        const headers = headersBuilder();
        const newPromises = newIds.map(async (id) => {
          try {
            const detailRes = await getCompanyDetailApi({
              id,
              headers,
              countView: true,
            });

            const [nielsenRes, agenciesRes, creaRes, entityNewsRes] =
              await Promise.all([
                getCompanyNielsenApi({ id, headers }).catch(() => ({
                  data: {},
                })),
                searchWorkRelationApi({
                  headers,
                  data: {
                    company_id: id,
                    pageNum: 1,
                    limit: 10,
                  },
                }).catch(() => ({ data: [] })),
                getCompanyCreaApi({ id, headers }).catch(() => ({ data: [] })),
                getEntityNewsForEntityApi({
                  entityType: "company",
                  entityId: id,
                }).catch(() => ({ data: [] })),
              ]);

            return {
              id,
              data: detailRes.data,
              schema: detailRes.schema,
              nielsen: nielsenRes,
              agencies: agenciesRes?.data || [],
              creas: creaRes?.data || creaRes || [],
              entityNews: entityNewsRes?.data || [],
              loading: false,
            };
          } catch (err) {
            return { id, error: err, loading: false };
          }
        });

        const newResults = await Promise.all(newPromises);

        setCompanies((prev) => {
          // Filter out IDs no longer in 'ids'
          const filteredPrev = prev.filter((c) => ids.includes(c.id));
          const allResults = [...filteredPrev, ...newResults];
          // Ensure the returned array follows the order of the 'ids' argument
          return ids
            .map((id) => allResults.find((res) => res.id === id))
            .filter(Boolean);
        });
      } catch (err) {
        console.error("Error loading multiple companies:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchNeeded();
  }, [JSON.stringify(ids)]);

  return { companies, loading, error };
};

export default useMultipleCompanyDetails;
